'use client';
import { useProduct } from '@/components/product/product-context';
import { StarFilled, StarHalf } from '@/components/shared/star-icons';

/**
 * Bundle specific title section.
 */
export default function TitleSection({ block, position }) {
  const { hide_review_stars, product_title, shipping_copy, _metadata } =
    block.title_section;
  const { productReviewData } = useProduct();
  const reviewCount = productReviewData?.data?.bottomline?.total_review;
  const formattedReviewCount =
    reviewCount > 1000
      ? reviewCount.toLocaleString('en-US', { minimumIntegerDigits: 2 })
      : reviewCount;

  // Round to the nearest .5 and determine full and half stars
  const roundedStarCount = (
    Math.round(parseFloat(productReviewData?.productRatingValue, 10) * 2) / 2
  ).toFixed(1);
  const fullStars = parseInt(roundedStarCount, 10);
  const requiresHalfStar = roundedStarCount.includes('.5');

  function handleStarsClick(e) {
    e.preventDefault();
    const reviewEl = document.querySelector(
      "[data-name='ratings-reviews-section']"
    );
    if (reviewEl) {
      const scrollOffset = reviewEl.children?.length ? 50 : -800;
      const topPosition = reviewEl.offsetTop - scrollOffset;
      window.scrollTo({ top: topPosition, left: 0, behavior: 'smooth' });

      // If using a keyboard, focus the review section
      if (e.detail < 1) {
        reviewEl.setAttribute('tabindex', '-1');
        reviewEl.focus({ preventScroll: true }); // Let the smooth scroll handle it
        reviewEl.removeAttribute('tabindex');
      }
    }
  }

  return (
    <section
      id={`${_metadata.uid}_${position}`}
      data-name="title-section"
      className="not-last:mb-8"
    >
      {product_title && <h1 className="h4 mb-2 text-white">{product_title}</h1>}
      {!hide_review_stars && reviewCount && (
        <button
          className="mb-2 flex min-h-6.25 min-w-25 items-center"
          onClick={handleStarsClick}
          aria-label={`${roundedStarCount} out of 5 stars based on ${formattedReviewCount} reviews. Scroll to reviews section`}
        >
          <div className="flex cursor-pointer items-center">
            {[...Array(fullStars).keys()].map((_, index) => (
              <StarFilled key={index} />
            ))}
            {requiresHalfStar && <StarHalf />}
          </div>
          <div className="body-xs pl-1.5 text-white underline hover:cursor-pointer">
            {formattedReviewCount} Reviews
          </div>
          <p
            className={`body-xs ${productReviewData.data.bottomline.total_review > 0 ? 'ml-3' : ''} text-[#EDCC6C]`}
          >
            {-Math.round(-productReviewData.totalLeesaReviewCount / 1000)}k+
            Leesa Reviews
          </p>
          <WavyCheck />
        </button>
      )}
      {shipping_copy?.length && (
        <div id="delivery-window" className="body-s text-light-green pt-2">
          <p className="pl-1">
            &#x2022;<span className="pl-2">{shipping_copy}</span>
          </p>
        </div>
      )}
    </section>
  );
}

const WavyCheck = () => (
  <svg
    enableBackground="new 0 0 16 16"
    viewBox="0 0 16 16"
    width="16"
    height="16"
    fill="#EDCC6C"
    className="ml-[5px]"
    aria-hidden={true}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="m14.6 7-.8-1c-.2-.2-.3-.5-.4-.9v-1.1c-.1-.7-.6-1.3-1.4-1.4l-1.2-.1c-.3 0-.6-.2-.9-.4l-.9-.7c-.6-.5-1.4-.5-2 0l-1 .8c-.2.2-.5.3-.8.3l-1.2.1c-.7.1-1.3.7-1.4 1.4l-.1 1.2c0 .3-.2.6-.4.9l-.7.9c-.5.6-.5 1.4 0 2l.8 1c.2.2.3.5.4.9v1.1c.1.7.6 1.3 1.4 1.4l1.2.1c.3 0 .6.2.9.4l1 .8c.6.5 1.4.5 1.9 0l1-.8c.2-.2.5-.3.9-.4l1.2-.1c.7-.1 1.3-.6 1.4-1.4l.1-1.2c0-.3.2-.6.4-.9l.8-1c.3-.5.3-1.3-.2-1.9zm-3.9-.2-3.1 3.1c-.1.1-.2.2-.4.2-.1 0-.3 0-.3-.2l-1.6-1.6c-.2-.2-.2-.5 0-.7s.5-.2.7 0l1.2 1.2 2.8-2.7c.2-.2.5-.2.7 0s.2.5 0 .7z" />
  </svg>
);
